import {
  RECORDS_PER_PAGE_MANUAL_SEARCH_RESULTS,
  RECORDS_PER_PAGE_SEARCH_RESULTS,
} from "../../../helper/constants";

interface LibraryItem {
  publicationId: string;
  [key: string]: any;
}

interface SelectedFacet {
  facetName: string;
  facetField: string;
  facetsSelectedValue: string[];
}

interface SearchPayload {
  queryString: string;
  pageable: {
    pageNo: string;
    pageSize: string;
    sortBy: string;
    sortDir: string;
  };
  selectedFacets?: SelectedFacet[];
}

interface SearchResponse {
  message: {
    isSuccess: boolean;
    result: any;
    statusCode?: number;
  };
}

/**
 * Fetches library data from the API
 */
export const fetchLibraryData = async (pageNumber = 1, pageSize = 100) => {
  try {
    const response = await fetch(`/api/v1/library`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        pageNo: pageNumber,
        pageSize: pageSize,
        searchParameter: "",
      }),
    });
    const data = await response.json();
    if (data?.message?.isSuccess === true && data?.message?.result?.result !== undefined) {
      return {
        data: data?.message?.result?.result,
        totalRecords: data?.message?.result?.totalRecordsFromBMS || 0,
        success: true
      };
    } else {
      return {
        data: [],
        totalRecords: 0,
        success: true
      };
    }
  } catch (error) {
    console.error('Library API error:', error);
    throw error;
  }
};

export const fetchAllLibraryData = async () => {

  let allLibraryData: any[] = [];
  const pageSize = 100;

  try {
    const firstResponse = await fetchLibraryData(1, pageSize);
    allLibraryData = [...firstResponse.data];

    const totalRecords = firstResponse.totalRecords;
    const totalPages = Math.ceil(totalRecords / pageSize);

    if (totalPages > 1) {
      for (let page = 2; page <= totalPages; page++) {
        const response = await fetchLibraryData(page, pageSize);
        allLibraryData = [...allLibraryData, ...response.data];
      }
    }
    return allLibraryData;
  } catch (error) {
    console.error('Error fetching all library data:', error);
    return [];
  }
};

const extractPubIds = (libraryData: any[]): string => {
  const pubIds = libraryData.map((item: any) =>
    item.pubId || item.publicationId || item.id
  );
  return pubIds.join(' ');
};
/**
 * Updated search function with better error handling and debugging
 */
export const searchWithManualMode = async (
  searchValue: string,
  pageNumber = 1,
  isNewSearch = true,
  pageSize: number = RECORDS_PER_PAGE_MANUAL_SEARCH_RESULTS

): Promise<SearchResponse> => {
  try {
    // Fetch library data to get publication IDs
    const libraryData = await fetchAllLibraryData();

    if (!libraryData || libraryData.length === 0) {
      console.warn('No library data available');
      return {
        message: {
          isSuccess: true,
          result: {
            hits: { total: { value: 0 }, hits: [] },
            aggregations: {}
          }
        }
      };
    }

    const publicationIdsString = extractPubIds(libraryData);

    if (!publicationIdsString || publicationIdsString.trim() === '') {
      console.warn('No publication IDs extracted from library data');

      return {
        message: {
          isSuccess: true,
          result: {
            hits: { total: { value: 0 }, hits: [] },
            aggregations: {}
          }
        }
      };
    }

    // Create search payload with the correct structure
    const payload: SearchPayload = {
      queryString: publicationIdsString, // Use library IDs as queryString
      pageable: {
        pageNo: pageNumber.toString(),
        pageSize: pageSize.toString(),
        sortBy: "",
        sortDir: "",
      },
      selectedFacets: [
        {
          facetName: "publication_category_name",
          facetField: "publication_category_name.publication_category",
          facetsSelectedValue: ["Manual"]
        }
      ]
    };


    const response = await fetch("/api/v1/search/search-results", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    return data;

  } catch (error) {
    console.error("Error in manual mode search:", error);
    return {
      message: {
        isSuccess: false,
        result: "Error performing manual mode search",
        statusCode: 500,
      },
    };
  }
};
/**
 * Search function that uses pre-fetched publication IDs (no library API calls)
 */
export const searchWithManualModeUsingCache = async (
  searchValue: string,
  pageNumber: number,
  pubIdsString: string,
  pageSize: number
): Promise<SearchResponse> => {
  try {

    const payload: SearchPayload = {
      queryString: `${searchValue} ${pubIdsString}`,
      pageable: {
        pageNo: pageNumber.toString(),
        pageSize: pageSize.toString(),
        sortBy: "",
        sortDir: ""
      },
      selectedFacets: [
        {
          facetName: "publication_category_name",
          facetField: "publication_category_name.publication_category",
          facetsSelectedValue: ["Manual"]
        }
      ]
    };

    const response = await fetch("/api/v1/search/search-results", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Error in manual search with cached data:", error);
    throw error;
  }
};